// Static demo-mode fill-in data for InvitationsHub.component.jsx.
// Shapes must match exactly what the two live Firestore `invites`
// onSnapshot listeners produce (`doc.id` spread onto `doc.data()`), and what
// this view's own render reads per row via `formatTimestamp`/`scopeLabel`:
//   { id, status, companyName, teamName/contextTeamName/teamId, scopeType,
//     invitedAs, createdAt: { seconds }, inviteeEmail, inviteeName,
//     invitedByEmail }
//
// `createdAt` mirrors a Firestore Timestamp closely enough for
// `formatTimestamp` (which accepts either a `.toDate()` value or a plain
// `{ seconds }` object) — a plain `{ seconds }` object is used here.

// Pending invites use a createdAt computed relative to "now" so they never
// look like they've sat unanswered for months — resolved invites
// (accepted/declined/revoked/expired) keep fixed historical dates since a
// past date is expected/correct for those statuses.
const DAY_SECONDS = 24 * 60 * 60;
const nowSeconds = () => Math.floor(Date.now() / 1000);

// Rendered on the "My invites" tab (invitations addressed to the demo user).
export const DEMO_MY_INVITES = [
  {
    id: 'demo-invite-my-1',
    status: 'pending',
    companyName: 'Acme Corp',
    scopeType: 'team',
    teamName: 'Marketing',
    invitedAs: 'Member',
    createdAt: { seconds: nowSeconds() - 3 * DAY_SECONDS }, // 3 days ago
    inviteeEmail: 'ryan.mitchell@meridianlabs.com',
    inviteeName: 'Ryan Mitchell',
    invitedByEmail: 'owner@acmecorp.com',
  },
  {
    id: 'demo-invite-my-2',
    status: 'accepted',
    companyName: 'Meridian Labs',
    scopeType: 'company_admin',
    invitedAs: 'Admin',
    createdAt: { seconds: 1782864000 }, // Jul 1, 2026
    inviteeEmail: 'ryan.mitchell@meridianlabs.com',
    inviteeName: 'Ryan Mitchell',
    invitedByEmail: 'admin@meridianlabs.com',
  },
  {
    id: 'demo-invite-my-3',
    status: 'declined',
    companyName: 'Globex Inc',
    scopeType: 'team',
    teamName: 'Customer Support',
    invitedAs: 'Member',
    createdAt: { seconds: 1780272000 }, // Jun 1, 2026
    inviteeEmail: 'ryan.mitchell@meridianlabs.com',
    inviteeName: 'Ryan Mitchell',
    invitedByEmail: 'hr@globex.example',
  },
  {
    id: 'demo-invite-my-4',
    status: 'expired',
    companyName: 'Initech',
    scopeType: 'team',
    teamName: 'Engineering',
    invitedAs: 'Manager',
    createdAt: { seconds: 1775001600 }, // Apr 1, 2026
    inviteeEmail: 'ryan.mitchell@meridianlabs.com',
    inviteeName: 'Ryan Mitchell',
    invitedByEmail: 'ops@initech.example',
  },
];

// Rendered on the "Sent invites" tab (invitations sent by the demo company,
// visible to Admin/Manager roles — DEMO_PROFILE is an Admin so this tab is
// always visible/testable).
export const DEMO_SENT_INVITES = [
  {
    id: 'demo-invite-sent-1',
    status: 'pending',
    companyName: 'Meridian Labs',
    scopeType: 'team',
    teamId: 'demo-team-engineering',
    teamName: 'Engineering',
    invitedAs: 'Member',
    createdAt: { seconds: nowSeconds() - 3 * DAY_SECONDS }, // 3 days ago
    inviteeEmail: 'alex.johnson@example.com',
    inviteeName: 'Alex Johnson',
    invitedByEmail: 'ryan.mitchell@meridianlabs.com',
  },
  {
    id: 'demo-invite-sent-2',
    status: 'pending',
    companyName: 'Meridian Labs',
    scopeType: 'company_admin',
    invitedAs: 'Admin',
    createdAt: { seconds: nowSeconds() - 6 * DAY_SECONDS }, // 6 days ago
    inviteeEmail: 'olivia.bennett@example.com',
    inviteeName: 'Olivia Bennett',
    invitedByEmail: 'ryan.mitchell@meridianlabs.com',
  },
  {
    id: 'demo-invite-sent-3',
    status: 'accepted',
    companyName: 'Meridian Labs',
    scopeType: 'team',
    teamId: 'demo-team-sales',
    teamName: 'Sales',
    invitedAs: 'Member',
    createdAt: { seconds: 1781481600 }, // Jun 15, 2026
    inviteeEmail: 'marcus.reed@example.com',
    inviteeName: 'Marcus Reed',
    invitedByEmail: 'ryan.mitchell@meridianlabs.com',
  },
  {
    id: 'demo-invite-sent-4',
    status: 'revoked',
    companyName: 'Meridian Labs',
    scopeType: 'team',
    teamId: 'demo-team-support',
    teamName: 'Customer Support',
    invitedAs: 'Manager',
    createdAt: { seconds: 1779408000 }, // May 22, 2026
    inviteeEmail: 'chloe.sanders@example.com',
    inviteeName: 'Chloe Sanders',
    invitedByEmail: 'ryan.mitchell@meridianlabs.com',
  },
  {
    id: 'demo-invite-sent-5',
    status: 'expired',
    companyName: 'Meridian Labs',
    scopeType: 'team',
    teamId: 'demo-team-support',
    teamName: 'Customer Support',
    invitedAs: 'Member',
    createdAt: { seconds: 1777593600 }, // May 1, 2026
    inviteeEmail: 'daniel.lee@example.com',
    inviteeName: 'Daniel Lee',
    invitedByEmail: 'ryan.mitchell@meridianlabs.com',
  },
];
